import React, { useEffect } from 'react';
import { Platform } from 'react-native';

import { useVoucherConfigLogic } from './useVoucherConfigLogic';
import Colors from '../../constants/Colors';
import Links from '../../constants/Links';
import { useAuthenticate } from '../../contexts/authFlow';
import { useParent } from '../../hooks/useChild';
import useColorScheme from '../../hooks/useColorScheme';
import { useMaxWidth } from '../../hooks/useResponsive';
import { handleLink } from '../../utils/common';
import { PrimaryButton, SecondaryButton } from '../Button';
import { Icon } from '../Icon';
import { TransparentRow } from '../Row';
import {
  TextH3,
  TextCta,
  TextBodySmall,
  TextFinePrint,
  TextBody,
} from '../StyledText';
import { FormInput } from '../StyledTextInput';
import { TransparentView } from '../Themed';

export default function VoucherConfigForm() {
  const theme = useColorScheme();
  const colors = Colors[theme];

  const logic = useVoucherConfigLogic();
  const isSmallDevice = useMaxWidth(900);

  const authenticate = useAuthenticate();

  const parent = useParent();

  useEffect(() => {
    if (parent.id) {
      logic.setSenderInfo({
        name: parent.firstName ?? '',
        email: parent.email ?? '',
      });
    }
  }, [parent]);

  if (logic.step === 'sender') {
    return (
      <>
        <TextH3 style={[{ color: colors.text, textAlign: 'center' }]}>
          Gift giver's information
        </TextH3>
        {!parent.id && (
          <TextCta style={{ marginBottom: 16 }}>
            <TextCta
              style={{ textDecorationLine: 'underline' }}
              onPress={() => {
                authenticate('LOGIN');
              }}
            >
              Log in
            </TextCta>{' '}
            to auto-fill your details or{' '}
            <TextCta
              style={{ textDecorationLine: 'underline' }}
              onPress={() => {
                authenticate();
              }}
            >
              Sign up
            </TextCta>
          </TextCta>
        )}
        <FormInput
          placeholder="Your first and last name"
          value={logic.senderInfo.name}
          onChangeText={(text) => {
            logic.setSenderInfo({ ...logic.senderInfo, name: text });
            if (logic.errors.name) {
              const newErrors = { ...logic.errors };
              delete newErrors.name;
              logic.setErrors(newErrors);
            }
          }}
          error={logic.errors.name}
        />
        <FormInput
          placeholder="Your email"
          value={logic.senderInfo.email}
          onChangeText={(text) => {
            logic.setSenderInfo({ ...logic.senderInfo, email: text });
            if (logic.errors.email) {
              const newErrors = { ...logic.errors };
              delete newErrors.email;
              logic.setErrors(newErrors);
            }
          }}
          error={logic.errors.email}
        />
        <PrimaryButton
          style={[{ backgroundColor: colors.cta }]}
          onPress={logic.handleNextStep}
          title="Next"
          testID="gift-giver-next-button"
        />
      </>
    );
  }
  if (logic.step === 'recipients') {
    return (
      <>
        <TextH3 style={[{ color: colors.text, textAlign: 'center' }]}>
          Gift recipient information
        </TextH3>
        <TextBodySmall style={{ textAlign: 'center' }}>
          Show your gratitude by gifting up to ten special people a $50 child
          care voucher through Bumo.
        </TextBodySmall>
        {logic.errors.recipients && (
          <TextBodySmall style={{ textAlign: 'center', color: 'red' }}>
            {logic.errors.recipients}
          </TextBodySmall>
        )}
        <TransparentView
          style={{
            flex: 1,
            width: '100%',
            paddingVertical: 16,
            paddingHorizontal: isSmallDevice ? 16 : 50,
            ...Platform.select({
              web: {
                overflowY: 'auto',
                overflowX: 'hidden',
              },
            }),
          }}
        >
          {logic.recipientEmails.map((email, index) => (
            <FormInput
              key={index}
              placeholder={`Recipient ${index + 1} email`}
              value={email}
              onChangeText={(text) => {
                logic.handleRecipientEmailChange(index, text);
              }}
              error={logic.errors[`recipient-${index}`]}
            />
          ))}
        </TransparentView>
        <TransparentRow
          style={{
            flex: 0,
            flexBasis: 'auto',
            width: '100%',
            justifyContent: 'space-between',
            paddingTop: isSmallDevice ? 10 : 20,
          }}
        >
          <SecondaryButton onPress={logic.handlePrevStep} title="Back" />
          <PrimaryButton onPress={logic.handleNextStep} title="Next" />
        </TransparentRow>
      </>
    );
  }
  if (logic.step === 'confirmation') {
    return (
      <>
        <TextH3 style={[{ color: colors.text, textAlign: 'center' }]}>
          Confirm your gift
        </TextH3>
        <TextBodySmall style={{ textAlign: 'center' }}>
          Show your gratitude by gifting up to ten special people a $50 child
          care voucher through Bumo.
        </TextBodySmall>
        <TextBodySmall style={{ width: '100%' }} bold>
          Your information:
        </TextBodySmall>
        <TextBodySmall style={{ width: '100%' }}>
          {logic.senderInfo.name}
        </TextBodySmall>
        <TextBodySmall style={{ width: '100%' }}>
          {logic.senderInfo.email}
        </TextBodySmall>

        <TextBodySmall style={{ width: '100%' }} bold>
          Recipient information:
        </TextBodySmall>
        {logic.recipientEmails.map((email, index) => (
          <TextBodySmall style={{ width: '100%' }} key={index}>
            {email}
          </TextBodySmall>
        ))}
        <TransparentView
          style={{
            flex: 1,
            width: '100%',
            marginVertical: 20,
            backgroundColor: Colors[theme].backgroundSecondary,
            padding: 16,
            borderRadius: 8,
          }}
        >
          Note: This voucher is valid for new users only. Existing users are not
          eligible.
        </TransparentView>
        <TransparentRow
          style={{
            flex: 0,
            flexBasis: 'auto',
            width: '100%',
            justifyContent: 'space-between',
            paddingTop: 20,
          }}
        >
          <SecondaryButton onPress={logic.handlePrevStep} title="Back" />
          <PrimaryButton onPress={logic.handleSubmit} title="Send" />
        </TransparentRow>
        <TextFinePrint bold style={{ color: colors.textLowContrast }}>
          <TextFinePrint
            style={{
              cursor: 'pointer',
              textDecorationLine: 'underline',
              color: colors.textLowContrast,
            }}
            onPress={() => {
              handleLink(Links.bumoCreditTerms);
            }}
          >
            Terms of use
          </TextFinePrint>{' '}
          apply
        </TextFinePrint>
      </>
    );
  }

  if (logic.step === 'success') {
    return (
      <TransparentView
        style={{
          alignItems: 'center',
          height: '100%',
          paddingTop: 30,
          gap: 12,
          paddingHorizontal: 20,
        }}
      >
        <Icon name="heart" color="#EA5482" style={{ width: 40, height: 40 }} />
        <TextH3 style={[{ color: colors.text }]}>Thank you!</TextH3>
        <TextBody style={{ textAlign: 'center' }}>
          Your Mother&apos;s Day gift vouchers have been sent successfully. A
          confirmation email has been sent to your email address as well.
        </TextBody>
      </TransparentView>
    );
  }
}
